import React, { useCallback, useRef } from 'react'
import { observer } from 'mobx-react'
import { Container } from 'typescript-ioc/es5'
import axios from 'axios'
import aqe from '@pushly/aqe'
import { SessionExpiryHost } from '@pushly/aqe/lib/components/session-expiry-host/session-expiry-host'
import { AppState } from '../../stores/app'
import { AuthService } from '../../services/auth'

/**
 * MobX observer that reads session state from AppState and passes it
 * as props to the AQE SessionExpiryHost component.
 *
 * Bridges the pufferfish MobX store layer to the framework-agnostic
 * session expiry orchestrator in AQE.
 */
export const SessionExpiryAdapter: React.FC = observer(() => {
    const appState = useRef(Container.get(AppState)).current
    const authService = useRef(Container.get(AuthService)).current

    const onExtend = useCallback(async (): Promise<boolean> => {
        return extendSession(`${aqe.defaults.publicApiDomain}`)
    }, [])

    const onSignOut = useCallback(async (): Promise<void> => {
        await authService.logout()
    }, [authService])

    const onReauth = useCallback((): void => {
        authService.logout(false)
    }, [authService])

    const onExpired = useCallback((): void => {
        appState.sessionExpired = true
    }, [appState])

    return (
        <SessionExpiryHost
            idleExpiresAt={appState.idleExpiresAt}
            absoluteExpiresAt={appState.absoluteExpiresAt}
            idleTimeoutDuration={appState.idleTimeoutDuration}
            absoluteTimeoutDuration={appState.absoluteTimeoutDuration}
            isAuthenticated={appState.isAuthenticated}
            onExtend={onExtend}
            onSignOut={onSignOut}
            onReauth={onReauth}
            onExpired={onExpired}
        />
    )
})

/**
 * Attempts to extend the session via POST /auth/refresh.
 * Retries on HTTP 409 (refresh already in progress) with a 2-second delay, up to 2 retries.
 * Re-throws 401 errors for the global interceptor to handle.
 */
async function extendSession(apiDomain: string): Promise<boolean> {
    const MAX_RETRIES = 2
    let attempts = 0

    while (attempts <= MAX_RETRIES) {
        try {
            await axios.post(`${apiDomain}/auth/refresh`, null, {
                bypassGlobalErrorHandlers: true,
                headers: { 'X-User-Activity': 'true' },
            })
            return true
        } catch (error: any) {
            if (error.response?.status === 409 && attempts < MAX_RETRIES) {
                attempts++
                await new Promise((resolve) => setTimeout(resolve, 2000))
                continue
            }
            if (error.response?.status === 401) {
                throw error
            }
            return false
        }
    }

    return false
}
